import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { getRecordTags, assignRecordTag, unassignRecordTag } from '@/services/api/tag.service'

export function useRecordTags(recordId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = ['record-tags', recordId]

  const query = useQuery({
    queryKey,
    queryFn: () => getRecordTags(recordId!),
    enabled: !!recordId,
    staleTime: 30 * 1000,
  })

  const assignMutation = useMutation({
    mutationFn: (tagId: string) => assignRecordTag(recordId!, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      // Also invalidate the record detail — the tags list there is populated server-side
      queryClient.invalidateQueries({ queryKey: ['record', recordId] })
      // The timeline gets a "tag added" entry
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })

  const unassignMutation = useMutation({
    mutationFn: (tagId: string) => unassignRecordTag(recordId!, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['record', recordId] })
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })

  return {
    recordTags: query.data ?? [],
    isLoading: query.isLoading,
    assignTag: assignMutation.mutate,
    assignTagAsync: assignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
    unassignTag: unassignMutation.mutate,
    isUnassigning: unassignMutation.isPending,
  }
}
